import React from 'react';
import { BarChart, Trash2, Layers, PiggyBank } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных сценариев досрочного погашения
 */
const ComparisonSidebar = ({ 
  savedScenarios, 
  deleteSavedScenario, 
  loadSavedScenario 
}) => {
  // Если нет сохраненных сценариев
  if (!savedScenarios || savedScenarios.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сравнение сценариев
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Сохраняйте различные варианты досрочного погашения, чтобы сравнить их эффективность. 
          Сохраненные сценарии будут отображаться здесь.
        </p>
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center">
          <PiggyBank className="w-5 h-5 text-green-500 mr-2" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Нажмите "Сохранить сценарий" после расчета, чтобы добавить вариант в сравнение.
          </p>
        </div>
      </div>
    );
  }

  // Получение отформатированной даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Получение названия типа досрочного погашения
  const getRepaymentTypeName = (type) => {
    return type === 'reduction' ? 'Уменьшение срока' : 'Уменьшение платежа';
  };

  // Получение названия периодичности
  const getFrequencyName = (frequency) => {
    const frequencyMap = {
      once: 'Разовое',
      monthly: 'Ежемесячно',
      quarterly: 'Ежеквартально',
      semi_annually: 'Раз в полгода',
      annually: 'Ежегодно'
    };
    return frequencyMap[frequency] || frequency;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сравнение сценариев
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedScenarios.length} {savedScenarios.length === 1 ? 'вариант' : savedScenarios.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedScenarios.map((scenario) => (
          <div 
            key={scenario.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {scenario.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedScenario(scenario.id)}
                  title="Загрузить сценарий"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedScenario(scenario.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Сумма погашения:</span>
                <span className="font-medium">
                  {formatCurrency(scenario.results.totalEarlyRepayment, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип:</span>
                <span>
                  {getRepaymentTypeName(scenario.results.earlyRepaymentType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Периодичность:</span>
                <span>
                  {getFrequencyName(scenario.results.earlyRepaymentFrequency)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Экономия:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {formatCurrency(scenario.results.savedInterest, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Эффективность:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {formatNumber(scenario.results.effectiveInterestSavings, 1)}%
                </span>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-500">
              Сохранен: {formatDate(scenario.date)}
            </div>
          </div>
        ))}
      </div>

      {savedScenarios.length > 1 && (
        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800">
          <h4 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Наиболее выгодный сценарий</h4>
          
          {(() => {
            // Находим сценарий с максимальной эффективностью
            const bestScenario = savedScenarios.reduce((prev, current) => 
              (prev.results.effectiveInterestSavings > current.results.effectiveInterestSavings) ? prev : current
            );
            
            return (
              <div className="text-xs text-green-600 dark:text-green-400">
                <p><strong>{bestScenario.name}</strong> с эффективностью <strong>{formatNumber(bestScenario.results.effectiveInterestSavings, 1)}%</strong></p>
                <p className="mt-1">
                  {getRepaymentTypeName(bestScenario.results.earlyRepaymentType)}, 
                  {getFrequencyName(bestScenario.results.earlyRepaymentFrequency).toLowerCase()}.
                  Экономия: {formatCurrency(bestScenario.results.savedInterest)}.
                </p>
              </div>
            );
          })()}
          
          <button
            onClick={() => loadSavedScenario(
              savedScenarios.reduce((prev, current) => 
                (prev.results.effectiveInterestSavings > current.results.effectiveInterestSavings) ? prev : current
              ).id
            )}
            className="mt-2 w-full py-1 text-xs bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-700 transition-colors"
          >
            Загрузить лучший вариант
          </button>
        </div>
      )}
    </div>
  );
};

export default ComparisonSidebar;